/**
 * Wires whichever auth mode is configured into the API client, then renders.
 *
 * Two modes, one client. The Entra strategy attaches a bearer token; the dev
 * strategy attaches `x-dev-user`. Everything downstream — the API client, the
 * `/api/me` hook, the tier checks — is identical, which is what makes the dev
 * path a genuine exercise of the real one rather than a parallel universe.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { InteractionRequiredAuthError, type IPublicClientApplication } from '@azure/msal-browser';
import { apiScopes, authMode, devLoginEnabled, loginRequest, type AuthMode } from './config';
import { currentTarget, encodeTarget } from './deep-link';
import { setAuthStrategy, type AuthStrategy } from '@/lib/api';

const DEV_USER_STORAGE_KEY = 'menlo-funnel.dev-user';

/** Matches the seeded users in `apps/api/src/seed/dataset.ts`. */
export const DEV_USERS = [
  { email: 'rep@menlomicro.example', label: 'Riley Rep', hint: 'Rep → User' },
  { email: 'fae@menlomicro.example', label: 'Fran Engineer', hint: 'FAE → User' },
  { email: 'exec@menlomicro.example', label: 'Evan Exec', hint: 'Exec → User' },
  { email: 'manager@menlomicro.example', label: 'Morgan Manager', hint: 'SalesManager → Manager' },
  { email: 'admin@menlomicro.example', label: 'Avery Admin', hint: 'Admin → Admin' },
  {
    email: 'repmanager@menlomicro.example',
    label: 'Robin Rep-Manager',
    hint: 'Rep + SalesManager → Manager',
  },
  { email: 'apac@menlomicro.example', label: 'Asha Apac', hint: 'Rep → User, Asia/Kolkata' },
  { email: 'inactive@menlomicro.example', label: 'Dana Deactivated', hint: '403' },
] as const;

interface AuthContextValue {
  /**
   * `misconfigured` reaches here so `App.tsx` can render the refusal screen.
   * Nothing in the provider treats it as a working mode: the strategy below
   * falls through to the dev one, which has no credentials to send, so every
   * request 401s — which is the correct outcome for a build that cannot sign
   * anyone in.
   */
  mode: AuthMode;
  devUser: string;
  setDevUser: (email: string) => void;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

export interface AuthProviderProps {
  children: ReactNode;
  /** Present only in Entra mode. */
  msal?: IPublicClientApplication;
  /** Bumped when credentials change, so TanStack Query refetches `/api/me`. */
  onIdentityChanged?: () => void;
}

export function AuthProvider({
  children,
  msal,
  onIdentityChanged,
}: AuthProviderProps): React.ReactElement {
  const [devUser, setDevUserState] = useState<string>(
    () => window.localStorage.getItem(DEV_USER_STORAGE_KEY) ?? DEV_USERS[0].email,
  );

  const signIn = useCallback(() => {
    if (!msal) return;
    // Stash where the user meant to go, so the digest deep link survives the
    // round trip (§8e).
    void msal.loginRedirect(loginRequest(encodeTarget(currentTarget())));
  }, [msal]);

  const signOut = useCallback(() => {
    if (!msal) {
      window.location.assign('/logout');
      return;
    }
    void msal.logoutRedirect();
  }, [msal]);

  const setDevUser = useCallback(
    (email: string) => {
      window.localStorage.setItem(DEV_USER_STORAGE_KEY, email);
      setDevUserState(email);
      onIdentityChanged?.();
    },
    [onIdentityChanged],
  );

  useEffect(() => {
    /**
     * `__DEV_LOGIN__` is a compile-time literal, so a deployed build reduces
     * this to the Entra strategy and drops `devStrategy` — and with it the
     * `x-dev-user` header — from the bundle entirely.
     *
     * When neither applies the mode is `misconfigured`, and the anonymous
     * strategy sends no credentials: every request 401s, which is the correct
     * outcome for a build that cannot sign anyone in.
     */
    const strategy: AuthStrategy | undefined =
      authMode === 'entra' && msal
        ? entraStrategy(msal, signIn)
        : __DEV_LOGIN__
          ? devStrategy(() => window.localStorage.getItem(DEV_USER_STORAGE_KEY) ?? devUser)
          : undefined;

    if (!strategy) return;

    setAuthStrategy(strategy);
  }, [msal, signIn, devUser]);

  const value = useMemo<AuthContextValue>(
    () => ({ mode: authMode, devUser, setDevUser, signIn, signOut }),
    [devUser, setDevUser, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function entraStrategy(msal: IPublicClientApplication, signIn: () => void): AuthStrategy {
  /**
   * MSAL caches tokens; do not cache one here as well. A second cache is how a
   * revoked session keeps working past its expiry, and how the "one silent
   * refresh" below silently becomes a no-op.
   */
  const acquire = async (forceRefresh: boolean): Promise<string | null> => {
    const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
    if (!account) return null;
    try {
      const result = await msal.acquireTokenSilent({ scopes: apiScopes, account, forceRefresh });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) return null;
      throw error;
    }
  };

  return {
    async headers(): Promise<Record<string, string>> {
      const token = await acquire(false);
      // No token means no header: let the request 401 and take the refresh
      // path, rather than sending `Authorization: Bearer null`.
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    async refresh() {
      return (await acquire(true)) !== null;
    },
    onAuthenticationLost() {
      // Falls back to the interactive redirect, preserving the current path.
      signIn();
    },
  };
}

/**
 * Install the dev strategy at **module load**, not in the provider's effect.
 *
 * `useMe` fires its first `/api/me` from a child effect, and React runs child
 * effects **before** the parent's — so a strategy installed in
 * `AuthProvider`'s own effect misses the very first request of every page
 * load. In Entra mode that is invisible: the request 401s, `refresh()`
 * acquires a token, and the retry succeeds. In `DEV_LOGIN` mode there is
 * nothing to refresh, so the 401 is final and the app shows its sign-in screen
 * to a user who is signed in — which is every page load of the mode §8d says
 * phases 1–5 are built on.
 *
 * The effect below still runs and still wins; this only makes sure a strategy
 * exists before the first request rather than just after it.
 */
if (__DEV_LOGIN__ && authMode === 'dev') {
  setAuthStrategy(
    devStrategy(() => window.localStorage.getItem(DEV_USER_STORAGE_KEY) ?? DEV_USERS[0].email),
  );
}

function devStrategy(getEmail: () => string): AuthStrategy {
  return {
    async headers() {
      return { 'x-dev-user': getEmail() };
    },
    // There is nothing to refresh: the header either names a seeded user or it
    // does not.
    async refresh() {
      return false;
    },
    onAuthenticationLost() {
      // Nowhere to send them. The dev switcher is on screen.
    },
  };
}

export { devLoginEnabled };
